import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { DealStage, DEAL_STAGES } from "@/types/dealTracker";

interface DealStageBarProps {
  currentStage: DealStage;
  onStageChange?: (stage: DealStage) => void;
  disabled?: boolean;
}

const DealStageBar = ({ currentStage, onStageChange, disabled = false }: DealStageBarProps) => {
  const currentIndex = DEAL_STAGES.findIndex(s => s.value === currentStage);
  
  return (
    <div className="w-full">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">Deal Stage</h3>
      
      {/* Desktop: Horizontal bar */}
      <div className="hidden md:block">
        {/* Circle row - fixed height container */}
        <div className="relative h-10 flex items-center justify-between">
          {/* Progress line - positioned at vertical center */}
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-border" />
          <div 
            className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-primary transition-all duration-300"
            style={{ width: `${(currentIndex / (DEAL_STAGES.length - 1)) * 100}%` }}
          />
          
          {DEAL_STAGES.map((stage, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            
            return (
              <button
                key={stage.value}
                onClick={() => !disabled && onStageChange?.(stage.value)}
                disabled={disabled}
                className={cn(
                  "relative z-10 w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all",
                  isCompleted && "bg-primary border-primary text-primary-foreground",
                  isCurrent && "bg-primary border-primary text-primary-foreground ring-4 ring-primary/20",
                  !isCompleted && !isCurrent && "bg-background border-border text-muted-foreground",
                  !disabled && "hover:border-primary hover:text-primary cursor-pointer",
                  disabled && "cursor-default"
                )}
              >
                {isCompleted ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Labels row - separate from circles */}
        <div className="flex justify-between mt-2">
          {DEAL_STAGES.map((stage, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            
            return (
              <span 
                key={stage.value}
                className={cn(
                  "text-xs text-center w-[80px]",
                  (isCompleted || isCurrent) ? "text-foreground font-medium" : "text-muted-foreground"
                )}
              >
                {stage.label}
              </span>
            );
          })}
        </div>
      </div>
      
      {/* Mobile: Vertical list with connecting line */}
      <div className="md:hidden">
        {DEAL_STAGES.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isLast = index === DEAL_STAGES.length - 1;
          const isLineCompleted = index < currentIndex;
          
          return (
            <div key={stage.value} className="relative">
              {/* Vertical connecting line (not on last item) */}
              {!isLast && (
                <div 
                  className={cn(
                    "absolute left-[35px] top-[56px] w-0.5 h-[calc(100%+12px-56px)]",
                    isLineCompleted ? "bg-primary" : "bg-border/50"
                  )}
                />
              )}
              
              {/* Stage card */}
              <button
                onClick={() => !disabled && onStageChange?.(stage.value)}
                disabled={disabled}
                className={cn(
                  "relative z-10 w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 mb-3",
                  isCompleted && "bg-gradient-to-r from-primary/15 to-primary/5 border-primary/50 shadow-[0_0_15px_rgba(255,215,0,0.15)]",
                  isCurrent && "bg-gradient-to-r from-primary to-yellow-500 border-primary shadow-[0_0_25px_rgba(255,215,0,0.4)]",
                  !isCompleted && !isCurrent && "bg-card/50 backdrop-blur-sm border-border/50",
                  !disabled && "hover:border-primary/70 hover:shadow-[0_0_20px_rgba(255,215,0,0.2)] cursor-pointer",
                  disabled && "cursor-default"
                )}
              >
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-lg transition-all",
                  isCompleted && "bg-primary text-primary-foreground shadow-[0_0_10px_rgba(255,215,0,0.3)]",
                  isCurrent && "bg-background text-primary shadow-[0_0_10px_rgba(255,215,0,0.5)]",
                  !isCompleted && !isCurrent && "bg-muted/80 text-muted-foreground"
                )}>
                  {isCompleted ? <Check className="h-5 w-5" /> : index + 1}
                </div>
                <span className={cn(
                  "text-base font-medium text-center flex-1",
                  isCurrent && "text-primary-foreground",
                  isCompleted && "text-primary",
                  !isCompleted && !isCurrent && "text-muted-foreground"
                )}>
                  {stage.label}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DealStageBar;
